import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import { useHost } from '../Runtime';
import { useGetSiteInfo } from './hooks/useGetSiteInfo';
import { DYNAMIC_FEATURE_TOGGLES, SiteInfo, SiteSettings } from './types';

/** Cookie the selected currency is persisted in, mirroring CarSPA. */
const CURRENCY_COOKIE = 'currency';
const CURRENCY_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const AB_TEST_CONFIG = {
  offerbox_pos_test: {
    featureToggle: 'enableOfferboxTest',
    variants: ['control', 'offerbox_on_top'],
    expiryDays: 30,
    paths: [
      '/mietwagen-muenchen.html',
      '/mietwagen-flughafen-palma-de-mallorca.html',
      '/mietwagen-frankfurt.html',
    ],
  },
  stage_image_visibility_test: {
    featureToggle: 'enableStageImageVisibilityTest',
    variants: ['control', 'better_stage_image_visibility'],
    expiryDays: 30,
    paths: [
      '/mietwagen-berlin.html',
      '/mietwagen-mallorca.html',
      '/mietwagen-usa.html',
    ],
  },
} as const;

type TestNames = keyof typeof AB_TEST_CONFIG;

const SiteInfoContext = createContext<SiteInfo | undefined>(undefined);

declare global {
  interface Window {
    featureToggles: Readonly<unknown>[];
  }
}

function addPushEventHandler<T>(array: T[], callback: (entry: T[]) => void) {
  const augmentedArray = array;
  augmentedArray.push = function arrayPush(...items) {
    const result = Array.prototype.push.call(array, ...items);
    callback(items);
    return result;
  };
}

function getFeatureToggles(raw: Readonly<unknown>[]) {
  return raw.reduce(
    (toggles, payload) => {
      if (!Array.isArray(payload)) {
        return toggles;
      }

      const [feature, value] = payload;
      if (!DYNAMIC_FEATURE_TOGGLES.includes(feature)) {
        return toggles;
      }

      return {
        ...toggles,
        [feature]: !!value,
      };
    },
    {} as Record<string, boolean>,
  );
}

const makeCookieName = (name: string): string => `featureToggles[${name}]`;

const useFeatureToggles = () => {
  const searchParams = useSearchParams();

  const [featureToggles, setFeatureToggles] = useState<Record<string, boolean>>(
    DYNAMIC_FEATURE_TOGGLES.reduce(
      (toggles, variant) => {
        const cookieName = makeCookieName(variant);
        if (searchParams?.has(cookieName)) {
          return {
            ...toggles,
            [variant]: searchParams.get(cookieName) === 'true',
          };
        }
        return toggles;
      },
      {} as Record<string, boolean>,
    ),
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.featureToggles = Array.isArray(window.featureToggles)
        ? window.featureToggles
        : [];

      // Use the functional update: this handler is registered only once, so a
      // captured `featureToggles` would drop toggles from earlier pushes.
      if (window.featureToggles.length) {
        const pushedToggles = getFeatureToggles(window.featureToggles);
        setFeatureToggles(previous => ({ ...previous, ...pushedToggles }));
      }

      addPushEventHandler(window.featureToggles, newToggles => {
        const pushedToggles = getFeatureToggles(newToggles);
        setFeatureToggles(previous => ({ ...previous, ...pushedToggles }));
      });
    }
  }, []);

  return featureToggles;
};

const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const setCookie = (name: string, value: string, domain?: string): void => {
  if (typeof document === 'undefined') return;
  // Only scope to the domain when the current host actually belongs to it —
  // otherwise the browser rejects the cookie (e.g. domain from the configured
  // site host while running on localhost). Require an exact or dot-boundary
  // match so e.g. `notexample.com` does not match `example.com`. A host-only
  // cookie works everywhere.
  const { hostname } = window.location;
  const useDomain =
    !!domain && (hostname === domain || hostname.endsWith(`.${domain}`));
  const domainSuffix = useDomain ? `; domain=${domain}` : '';
  document.cookie = `${name}=${value}; max-age=${CURRENCY_COOKIE_MAX_AGE}; path=/${domainSuffix}`;
};

const useAbTests = (toggles: Record<string, boolean>, currentPath: string) =>
  useMemo(
    () =>
      (Object.keys(AB_TEST_CONFIG) as TestNames[]).reduce(
        (tests, testName) => {
          if (typeof window === 'undefined') {
            return tests;
          }

          const config = AB_TEST_CONFIG[testName];

          if (config.featureToggle && !toggles[config.featureToggle]) {
            return tests;
          }

          const isEnabledPath = (config.paths as readonly string[]).includes(
            currentPath,
          );

          if (!isEnabledPath) {
            return tests;
          }

          const searchParams = new URLSearchParams(window.location.search);
          const urlValue = searchParams.get(testName);
          const cookieValue = getCookie(testName);

          if (urlValue || cookieValue) {
            const value = urlValue || cookieValue || config.variants[0];
            return { ...tests, [testName]: value };
          }

          const randomIndex = Math.floor(
            Math.random() * config.variants.length,
          );
          const newVariant = config.variants[randomIndex];

          const date = new Date();
          date.setTime(
            date.getTime() + config.expiryDays * 24 * 60 * 60 * 1000,
          );
          document.cookie = `${testName}=${newVariant}; expires=${date.toUTCString()}; path=/`;

          return { ...tests, [testName]: newVariant };
        },
        {} as Record<TestNames, string | undefined>,
      ),
    [toggles, currentPath],
  );

type SiteInfoProviderProps = React.PropsWithChildren<{
  settingsDefaults?: Partial<SiteSettings>;
}>;

const SiteInfoProvider = ({
  children,
  settingsDefaults,
}: SiteInfoProviderProps) => {
  const siteInfo = useGetSiteInfo(settingsDefaults);
  const dynamicToggles = useFeatureToggles();
  const { cookieDomain } = useHost();

  // The currency chosen in the regional settings. `undefined` means the user
  // has not chosen one, so we fall back to the site's default currency.
  const [userCurrency, setUserCurrency] = useState<string | undefined>(
    undefined,
  );

  const setSelectedCurrency = useCallback(
    (currency: string) => {
      setUserCurrency(currency);
      setCookie(CURRENCY_COOKIE, currency, cookieDomain);
    },
    [cookieDomain],
  );

  // Restore a previously persisted currency once the site settings (and with
  // them the available exchange rates) are loaded, ignoring stale cookies for
  // currencies the site no longer offers.
  useEffect(() => {
    if (!siteInfo.settingsLoaded) {
      return;
    }
    const persistedCurrency = getCookie(CURRENCY_COOKIE);
    if (
      persistedCurrency &&
      siteInfo.settings.exchangeRates[persistedCurrency] !== undefined
    ) {
      setUserCurrency(persistedCurrency);
    }
  }, [siteInfo.settingsLoaded, siteInfo.settings.exchangeRates]);

  const selectedCurrency = userCurrency ?? siteInfo.settings.selectedCurrency;

  const allToggles = useMemo(
    () => ({
      ...siteInfo.featureToggles,
      ...dynamicToggles,
    }),
    [siteInfo.featureToggles, dynamicToggles],
  );
  const { asPath } = useRouter();
  const abTests = useAbTests(allToggles, asPath);

  const [isAbTestReady, setIsAbTestReady] = useState(false);

  useEffect(() => {
    setIsAbTestReady(true);
  }, []);

  useEffect(() => {
    if (isAbTestReady && typeof window !== 'undefined') {
      (Object.keys(abTests) as TestNames[]).forEach(testId => {
        const variant = abTests[testId];

        if (variant && variant !== '') {
          const eventKey = `ev_fired_${testId}_${variant}`;

          // Ad-hoc "already fired" markers parked on `window`, keyed per
          // test/variant, so they cannot be declared up front.
          const firedEvents = window as unknown as Record<string, boolean>;

          if (!firedEvents[eventKey]) {
            firedEvents[eventKey] = true;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'experiment_viewed',
              experiment_id: testId,
              variant,
            });
          }
        }
      });
    }
  }, [isAbTestReady, abTests]);

  const context = useMemo(
    () => ({
      ...siteInfo,
      featureToggles: {
        ...siteInfo.featureToggles,
        ...dynamicToggles,
      },
      dynamicFeatureToggles: dynamicToggles,
      abTests,
      isAbTestReady,
      selectedCurrency,
      setSelectedCurrency,
    }),
    [
      siteInfo,
      dynamicToggles,
      abTests,
      isAbTestReady,
      selectedCurrency,
      setSelectedCurrency,
    ],
  );

  return (
    <SiteInfoContext.Provider value={context}>
      {children}
    </SiteInfoContext.Provider>
  );
};

export { SiteInfoContext, SiteInfoProvider };
